import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Button, CircularProgress, Stack } from '@mui/material'
import CustomTable from 'common/components/CustomTable'
import { enqueueSnackbar } from 'notistack'
import { useContext, useRef, useState } from 'react'
import { CustomThemeContext } from 'src/common/providers/CustomThemeProvider'
import i18n from 'src/lang/idiomas'
import OrderLinesHelper from '../helpers/orderLinesHelper'
import ConfirmationPopup from './ConfirmationPopup'
import OrderLineSkeleton from './skeleton/OrderLineSkeleton'

/**
 * @typedef {Object}OrderLines
 */

/**
 * The upload file params
 * @component
 *
 * @param {Object} props
 * @param {function} [props.setOrderLines]
 * @param {Array} [props.orderLines]
 * @param {Array} [props.selectedOrderLines]
 * @param {function} [props.refetch]
 * @param {boolean} [props.loading]
 * @returns {JSX.Element}
 */

const OrderLines = ({
  setOrderLines = (ids) => undefined,
  selectedOrderLines = [],
  orderLines,
  refetch,
  loading,
}) => {
  const { themeController } = useContext(CustomThemeContext)
  const [deleteConfirmaton, setDeleteConfirmation] = useState(false)
  const [deleteAllCnf, setDeleteAllCnf] = useState(false)
  const [deleteLoader, setDeleteLoader] = useState(null)
  const [deleteAllLoder, setDeleteAllLoader] = useState(false)

  const selectedIdRef = useRef(null)

  const deleteOrderLineHandler = () => {
    setDeleteLoader(selectedIdRef.current)
    OrderLinesHelper.deleteOrderLine(selectedIdRef.current)
      .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }))
      .finally(() => {
        setDeleteLoader(null)
        refetch()
      })
    selectedIdRef.current = null
  }
  const deleteAllRowHandler = () => {
    setDeleteAllLoader((prev) => !prev)
    OrderLinesHelper.deleteAllOrderLines()
      .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }))
      .finally(() => {
        setDeleteAllLoader((prev) => !prev)
        refetch()
      })
  }

  const isSelectableRow = (row) => {
    return row.enabled ?? true
  }

  return (
    <>
      {loading && orderLines.length === 0 ? (
        <OrderLineSkeleton rows={7} columns={14} />
      ) : (
        <Stack>
          <Button
            key="delete-all-row"
            onClick={() => setDeleteAllCnf(true)}
            disabled={deleteAllLoder}
            sx={{
              color: '#fd0404d6',
              alignSelf: 'flex-end',
            }}
          >
            {deleteAllLoder && <CircularProgress size={20} sx={{ position: 'absolute' }} />}
            <DeleteForeverIcon sx={{ mr: 3 }} />
            {i18n.t('efactura.bortodas', { defaultValue: 'BORRAR TODAS' })}
          </Button>
          <CustomTable
            hideFooter={false}
            hideColumnFilter={true}
            density="compact"
            loading={loading}
            initialPageSize={20}
            showToolbar={true}
            showQuickFilter={false}
            dataSource={orderLines}
            checkbox={true}
            isRowSelectable={(params) => {
              return isSelectableRow(params.row)
            }}
            getRowClassName={(params) => (!isSelectableRow(params.row) ? 'row-disabled' : '')}
            onSelectionChange={(ids) => setOrderLines(ids)}
            rowSelectionModel={selectedOrderLines}
            fields={[
              {
                value: 'serial_number',
                key: i18n.t('efactura.numser', { defaultValue: 'Nº. serie' }),
                width: 70,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.serial_number,
              },
              {
                value: 'order_number',
                key: i18n.t('efactura.numped', { defaultValue: 'Nº. pedido' }),
                width: 80,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.order_number,
              },
              {
                value: 'line_number',
                key: i18n.t('efactura.linea', { defaultValue: 'Línea' }),
                width: 50,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.line_number,
              },
              {
                value: 'product_code',
                key: i18n.t('efactura.producto', { defaultValue: 'Producto' }),
                width: 90,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.product_code,
              },
              {
                value: 'product_description',
                key: i18n.t('efactura.descripc', { defaultValue: 'Descripción' }),
                width: 120,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.product_description,
              },
              {
                value: 'quantity',
                key: i18n.t('efactura.efqty', { defaultValue: 'Cantidad' }),
                width: 80,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.quantity,
              },
              {
                value: 'numero_doc_interno',
                key: i18n.t('efactura.nudocint', { defaultValue: 'Nº. Interno' }),
                width: 90,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.numero_doc_interno,
              },
              {
                value: 'numero_doc_ext',
                key: i18n.t('efactura.nudocext', { defaultValue: 'Nº. Externo' }),
                width: 90,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.numero_doc_ext,
              },
              {
                value: 'numero_linea_albaran',
                key: i18n.t('efactura.nulialb', { defaultValue: 'Nº. Línea albarán' }),
                width: 100,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.numero_linea_albaran,
              },
              {
                value: 'presentacion',
                key: i18n.t('efactura.presenta', { defaultValue: 'Presentación' }),
                width: 100,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.presentacion,
              },
              {
                value: 'importe_lin_neto',
                key: i18n.t('efactura.implinea', { defaultValue: 'Importe línea' }),
                width: 100,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.importe_lin_neto,
              },
              {
                value: 'cantidad_entregada',
                key: i18n.t('efactura.cantentr', { defaultValue: 'Cantidad entregada' }),
                width: 140,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.cantidad_entregada,
              },
              {
                value: 'total_entregado',
                key: i18n.t('efactura.imtoentr', { defaultValue: 'Importe total entregado' }),
                width: 170,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.total_entregado,
              },
              {
                value: 'centro_contable',
                key: i18n.t('efactura.centcont', { defaultValue: 'Centro Contable' }),
                width: 100,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.centro_contable ?? true,
              },
              {
                value: 'forma_pago',
                key: i18n.t('efactura.formpago', { defaultValue: 'Forma pago' }),
                width: 100,
                sortable: true,
                flex: 1,
                hide: themeController.hide.SendInvoice?.OrderLines?.forma_pago ?? true,
              },
              {
                value: 'actions',
                key: i18n.t('general.borrar', { defaultValue: 'Borrar' }),
                width: 100,
                flex: 1,
                filterable: false,
                sortable: false,
                renderCell: (order) => {
                  return (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteConfirmation(true)
                        selectedIdRef.current = order.id
                      }}
                      disabled={deleteLoader === order.id}
                      sx={{ color: '#fd0404d6', width: '100%', padding: '3px' }}
                    >
                      {deleteLoader === order.id && (
                        <CircularProgress size={20} sx={{ position: 'absolute' }} />
                      )}
                      <DeleteForeverIcon />
                      {i18n.t('general.borrar', { defaultValue: 'Borrar' })}
                    </Button>
                  )
                },
              },
            ]}
          />

          <ConfirmationPopup
            key="delete-single-row-cnf"
            confirm={deleteOrderLineHandler}
            open={deleteConfirmaton}
            title={i18n.t('efactura.borlinpd', { defaultValue: 'Borrar línea de pedido' })}
            description={i18n.t('efactura.cborlinp', {
              defaultValue: '¿Está seguro de querer borrar la línea de pedido?',
            })}
            closePopup={() => {
              setDeleteConfirmation(false)
              selectedIdRef.current = null
            }}
          />
          <ConfirmationPopup
            key="delete-all-row-cnf"
            confirm={deleteAllRowHandler}
            open={deleteAllCnf}
            title={i18n.t('efactura.bortlinp', {
              defaultValue: 'Borrar todas las líneas de pedido',
            })}
            description={i18n.t('efactura.cbrtlinp', {
              defaultValue: '¿Está seguro de querer borrar todas las líneas de pedido?',
            })}
            closePopup={() => {
              setDeleteAllCnf(false)
            }}
          />
        </Stack>
      )}
    </>
  )
}

export default OrderLines
